// src/pages/admin/AdminStatistics.jsx
// Statistiques & Analytique — espace ADMIN (toute la plateforme).
// Endpoint : GET /admin/analytics/overview?days=7|30|90   (voir backend AdminStatsController)
import { useEffect, useMemo, useState } from 'react'
import {
  Users, FileText, Eye, MousePointer, UserPlus, Radio, ShoppingBag, Wallet, X,
} from 'lucide-react'
import Badge from '../../components/ui/Badge'
import api from '../../lib/axios'
import {
  PALETTE, fmtNum, fmtMoney, fmtDateTime,
  RangeTabs, KpiCard, Section, TrendChart, SeriesToggles, BarList, DonutChart,
  EmptyHint, PageLoader, Warnings, ExportButton, downloadSeriesCsv,
} from '../../components/stats/StatsKit'

const TRAFFIC_SERIES = [
  { key: 'views',  label: 'Vues',         color: PALETTE[0] },
  { key: 'clicks', label: 'Clics',        color: PALETTE[1] },
  { key: 'scans',  label: 'Scans NFC',    color: PALETTE[2] },
  { key: 'leads',  label: 'Leads',        color: PALETTE[3] },
  { key: 'users',  label: 'Inscriptions', color: PALETTE[4] },
]

const apiError = (err, fallback) =>
  err?.response?.data?.message ||
  (err?.response?.status === 404
    ? "La route /admin/analytics/overview n'existe pas encore côté serveur."
    : '') ||
  fallback

// Métrique par défaut : { value, change }
const metric = (m) => ({ value: m?.value ?? 0, change: m?.change ?? null })

export default function AdminStatistics() {
  const [days, setDays] = useState(30)
  const [data, setData] = useState(null)
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [active, setActive] = useState(['views', 'scans'])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api
      .get('/admin/analytics/overview', { params: { days } })
      .then(({ data: res }) => {
        if (cancelled) return
        setData(res?.data ?? null)
        setMeta(res?.meta ?? null)
      })
      .catch((err) => {
        if (cancelled) return
        console.error(err)
        setError(apiError(err, 'Impossible de charger les statistiques.'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [days])

  const toggle = (key) =>
    setActive((prev) => {
      if (prev.includes(key)) return prev.length === 1 ? prev : prev.filter((k) => k !== key)
      return [...prev, key]
    })

  // Données normalisées (évite les crashs si une clé manque)
  const series = data?.series ?? {}
  const users = data?.users ?? {}
  const pages = data?.pages ?? {}
  const traffic = data?.traffic ?? {}
  const cards = data?.cards ?? {}
  const orders = data?.orders ?? null
  const subscriptions = data?.subscriptions ?? null
  const breakdowns = data?.breakdowns ?? []
  const topPages = data?.top_pages ?? []
  const topClients = data?.top_clients ?? []
  const topCards = data?.top_cards ?? []
  const recentUsers = users.recent ?? []
  const byRole = users.by_role ?? []

  const trafficSeries = useMemo(
    () =>
      data
        ? TRAFFIC_SERIES.filter((s) => active.includes(s.key)).map((s) => ({
            ...s,
            data: data.series?.[s.key] || [],
          }))
        : [],
    [data, active]
  )

  const revenueSeries = useMemo(
    () =>
      data?.series?.revenue?.length
        ? [{ key: 'revenue', label: "Chiffre d'affaires", color: PALETTE[2], data: data.series.revenue }]
        : [],
    [data]
  )

  const ordersSeries = useMemo(
    () =>
      data?.series?.orders?.length
        ? [{ key: 'orders', label: 'Commandes', color: PALETTE[1], data: data.series.orders }]
        : [],
    [data]
  )

  const exportCsv = () => {
    if (!data) return
    const list = [
      ...TRAFFIC_SERIES.map((s) => ({ label: s.label, data: series[s.key] || [] })),
      { label: 'Commandes', data: series.orders || [] },
      { label: "Chiffre d'affaires", data: series.revenue || [] },
    ].filter((s) => s.data.length)
    downloadSeriesCsv(
      `statistiques-admin-${days}j-${new Date().toISOString().slice(0, 10)}.csv`,
      list
    )
  }

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="text-xs font-semibold text-teal uppercase tracking-wider">
            Administration
          </span>
          <h1 className="text-3xl font-bold text-navy mt-1">Statistiques de la plateforme</h1>
          <p className="text-sm text-gray-500 mt-2 max-w-2xl">
            Vue d'ensemble de l'activité : clients, trafic, cartes NFC, commandes et chiffre
            d'affaires.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RangeTabs value={days} onChange={setDays} />
          <ExportButton onClick={exportCsv} disabled={!data} />
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading && !data ? (
        <PageLoader />
      ) : data ? (
        <>
          <Warnings items={meta?.warnings} />

          {/* KPI */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Nouveaux clients"
              value={fmtNum(metric(users.new).value)}
              change={metric(users.new).change}
              icon={Users}
              tone="teal"
              hint={`${fmtNum(users.total ?? 0)} comptes au total`}
            />
            <KpiCard
              label="Pages créées"
              value={fmtNum(metric(pages.new).value)}
              change={metric(pages.new).change}
              icon={FileText}
              tone="navy"
              hint={`${fmtNum(pages.total ?? 0)} pages${
                pages.published != null ? ` · ${fmtNum(pages.published)} publiées` : ''
              }`}
            />
            <KpiCard
              label="Vues de pages"
              value={fmtNum(metric(traffic.views).value)}
              change={metric(traffic.views).change}
              icon={Eye}
              tone="gold"
            />
            <KpiCard
              label="Clics sur liens"
              value={fmtNum(metric(traffic.clicks).value)}
              change={metric(traffic.clicks).change}
              icon={MousePointer}
              tone="teal"
            />
            <KpiCard
              label="Leads reçus"
              value={fmtNum(metric(traffic.leads).value)}
              change={metric(traffic.leads).change}
              icon={UserPlus}
              tone="navy"
            />
            <KpiCard
              label="Scans NFC"
              value={fmtNum(metric(cards.scans).value)}
              change={metric(cards.scans).change}
              icon={Radio}
              tone="gold"
              hint={`${fmtNum(cards.active ?? 0)} cartes actives sur ${fmtNum(cards.total ?? 0)}`}
            />
            {orders?.count && (
              <KpiCard
                label="Commandes"
                value={fmtNum(metric(orders.count).value)}
                change={metric(orders.count).change}
                icon={ShoppingBag}
                tone="teal"
              />
            )}
            {orders?.revenue && (
              <KpiCard
                label="Chiffre d'affaires"
                value={fmtMoney(metric(orders.revenue).value)}
                change={metric(orders.revenue).change}
                icon={Wallet}
                tone="navy"
                hint={
                  orders.avg_basket != null
                    ? `Panier moyen ${fmtMoney(orders.avg_basket)}`
                    : undefined
                }
              />
            )}
          </div>

          {/* TRAFIC */}
          <Section
            title="Activité de la plateforme"
            subtitle={`Du ${meta?.from ?? '—'} au ${meta?.to ?? '—'}`}
            right={<SeriesToggles items={TRAFFIC_SERIES} active={active} onToggle={toggle} />}
          >
            <TrendChart series={trafficSeries} />
          </Section>

          {/* VENTES */}
          {(revenueSeries.length > 0 || ordersSeries.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {revenueSeries.length > 0 && (
                <Section title="Chiffre d'affaires" subtitle="Par jour">
                  <TrendChart series={revenueSeries} formatValue={fmtMoney} />
                </Section>
              )}
              {ordersSeries.length > 0 && (
                <Section title="Commandes" subtitle="Par jour">
                  <TrendChart series={ordersSeries} />
                </Section>
              )}
            </div>
          )}

          {/* CLASSEMENTS */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Section title="Pages les plus vues" subtitle="Toute la plateforme">
              <BarList
                items={topPages.map((p) => ({
                  label: p.title,
                  sub: p.slug ? `/${p.slug}` : undefined,
                  value: p.views,
                }))}
                empty="Aucune vue sur cette période."
              />
            </Section>
            <Section title="Clients les plus actifs" subtitle="Vues cumulées de leurs pages">
              <BarList items={topClients} color={PALETTE[1]} empty="Aucune donnée client." />
            </Section>
            <Section title="Cartes les plus scannées" subtitle="Par code court">
              <BarList items={topCards} color={PALETTE[2]} empty="Aucun scan sur cette période." />
            </Section>
          </div>

          {/* RÉPARTITIONS */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Section
              title="Parc de cartes"
              subtitle={`${fmtNum(cards.total ?? 0)} cartes enregistrées`}
            >
              <DonutChart
                items={[
                  { label: 'Actives', value: cards.active ?? 0 },
                  { label: 'Verrouillées', value: cards.disabled ?? 0 },
                  ...(cards.unassigned != null
                    ? [{ label: 'Non attribuées', value: cards.unassigned }]
                    : []),
                ].filter((i) => i.value > 0)}
                empty="Aucune carte."
              />
            </Section>

            {orders?.by_status?.length > 0 && (
              <Section title="Commandes par statut" subtitle="Sur la période">
                <DonutChart items={orders.by_status} />
              </Section>
            )}

            {subscriptions && (
              <Section
                title="Abonnements"
                subtitle={`${fmtNum(subscriptions.total ?? 0)} au total`}
              >
                <DonutChart items={subscriptions.by_status ?? []} empty="Aucun abonnement." />
              </Section>
            )}

            {byRole.length > 0 && (
              <Section title="Comptes par rôle" subtitle={`${fmtNum(users.total ?? 0)} comptes`}>
                <DonutChart items={byRole} />
              </Section>
            )}

            {breakdowns.map((b) => (
              <Section key={b.key} title={b.title} subtitle="Répartition des vues">
                <DonutChart items={b.items ?? []} />
              </Section>
            ))}
          </div>

          {/* DERNIÈRES INSCRIPTIONS */}
          <Section title="Dernières inscriptions" subtitle="Les 6 comptes les plus récents">
            {recentUsers.length === 0 ? (
              <EmptyHint>Aucun compte.</EmptyHint>
            ) : (
              <ul className="divide-y divide-gray-50">
                {recentUsers.map((u) => (
                  <li key={u.id} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-teal-50 text-teal flex items-center justify-center text-xs font-bold shrink-0">
                        {(u.name || '?').slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-navy truncate">{u.name}</p>
                        {u.email && <p className="text-xs text-gray-400 truncate">{u.email}</p>}
                      </div>
                    </div>
                    <Badge variant="gray">{fmtDateTime(u.created_at)}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      ) : null}
    </div>
  )
}